import { useUser } from 'contexts/UserContext'
import useFetch from 'hooks/useFetch'
import { useToast } from 'hooks/useToast'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { ImSpinner9 } from 'react-icons/im'
import { LuMoveLeft } from 'react-icons/lu'
import { useLocation } from 'wouter'
import FranquiciasFormDetail from 'forms/Franquicias/FranquiciasFormDetail'

const emptyFields = {
  codigo_franquicia: '',
  codigo_fq_3dig: '',
  nombre: '',
  propiedad_franquicia: '',
  cif_nif: '',
  iban: '',
  email: '',
  columna1: '',
  columna2: ''
}

const normalizeFetched = (row) => {
  const out = { ...(row || {}) }
  for (const k of Object.keys(emptyFields)) {
    if (out[k] === null || out[k] === undefined) out[k] = ''
    // NO trim: respetar espacios
    out[k] = typeof out[k] === 'string' ? out[k] : out[k]
  }
  return out
}

export default function FranquiciasViewDetail({ params }) {
  const { showToast } = useToast()
  const { user } = useUser()
  const [, setLocation] = useLocation()

  const [fetched, fetchOne] = useFetch()
  const [saved, saveOne] = useFetch()
  const [deleted, deleteOne] = useFetch()

  const isNew = params?.codigo === 'new'
  const [fields, setFields] = useState(emptyFields)
  const ref = useRef()

  useEffect(() => {
    if (!isNew) {
      fetchOne({
        url: `franquicias/${params.codigo}`,
        method: 'GET',
        messageKo: 'Error cargando franquicia'
      })
    } else {
      setFields(emptyFields)
    }
  }, [isNew, params?.codigo, fetchOne])

  useEffect(() => {
    if (fetched.data) setFields({ ...emptyFields, ...normalizeFetched(fetched.data) })
    if (fetched.error) setLocation('/page-not-found')
  }, [fetched.data, fetched.error, setLocation])

  const handleSubmit = async (values) => {
    const payload = { ...values }

    let url = `franquicias`
    let method = 'POST'

    if (!isNew) {
      method = 'PUT'
      url += `/${params.codigo}`
      payload.codigo_franquicia = params.codigo
    } else {
      url += `/single`
    }

    await saveOne({ url, method, body: payload })
  }

  useEffect(() => {
    if (!saved.isCalled) return

    if (saved.message) {
      showToast(saved.message, 'success')
      setLocation('/franquicias/grid')
      return
    }
    if (!saved.message && saved.data) {
      showToast('Guardado correctamente', 'success')
      setLocation('/franquicias/grid')
      return
    }
    if (saved.error) showToast(saved.error, 'error')
  }, [saved.isCalled, saved.message, saved.data, saved.error, showToast, setLocation])

  const handleDelete = async (codigo) => {
    await deleteOne({
      url: `franquicias/${codigo}`,
      method: 'DELETE',
      messageKo: 'Error eliminando franquicia'
    })
  }

  useEffect(() => {
    if (!deleted.isCalled) return

    if (deleted.message) {
      showToast(deleted.message, 'success')
      setLocation('/franquicias/grid')
      return
    }
    if (!deleted.message && deleted.data) {
      showToast('Eliminado correctamente', 'success')
      setLocation('/franquicias/grid')
      return
    }
    if (deleted.error) showToast(deleted.error, 'error')
  }, [deleted.isCalled, deleted.message, deleted.data, deleted.error, showToast, setLocation])

  return isNew || (!isNew && fields.codigo_franquicia) ? (
    <>
      <div className="sm:flex sm:items-start sm:justify-between">
        <div className="flex items-start gap-x-3">
          <h2 className="text-lg font-medium text-gray-800 dark:text-white">
            Franquicia ({fields?.codigo_franquicia ? `#${fields.codigo_franquicia}` : 'nuevo'})
          </h2>
        </div>

        <div className="flex items-center mt-4 sm:mt-0 gap-x-4">
          <button
            title="Volver"
            type="button"
            onClick={() => setLocation('/franquicias/grid')}
            className="flex items-center justify-center w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 gap-x-2 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-[#FCAE52] focus:border-[#FCAE52] dark:focus:border-[#FCAE52]"
          >
            <LuMoveLeft size={20} />
            <span>Volver</span>
          </button>
        </div>
      </div>

      <FranquiciasFormDetail
        isNew={isNew}
        user={user}
        fields={fields}
        franquiciaRef={ref}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
      />
    </>
  ) : (
    <div className="flex items-center content-center justify-center w-full h-full">
      <p className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700" title="Cargando...">
        <ImSpinner9 size={70} className="animate-spin" />
      </p>
    </div>
  )
}

FranquiciasViewDetail.propTypes = {
  params: PropTypes.any
}
